import {
	BillNotifyTimelineItem,
	REQUEST_SOURCE,
	RequestSourceMap,
	VendingStatusMap,
	VendingTokenTimelineItem,
	vendingStatusClassName,
} from "../../../../model/transaction";

// Normalized timeline row - vending tokens and bill notify attempts both map into this
export interface TimelineEntry {
	id: string;
	title: string;
	createdAt: string;
	statusLabel: string;
	statusClass: string; // scss key: completed | failed | failed-with-refund | in-progress
	iconState: "completed" | "pending" | "failed";
	meta: string;
	// raw: render the value as-is, without the "—" fallback for empty values
	rawFields: Array<{ label: string; value?: string; raw?: boolean }>;
}

// "System | Automatic", "Utility Admin | Jane Doe", "System | Attempt 2 Of 3"
const buildTokenMeta = (index: number, total: number, requestSource: number, requestedByName?: string): string => {
	const isSystem = requestSource === REQUEST_SOURCE.SYSTEM;
	const isLast = index === total - 1;
	const source = index === 0 || isSystem ? "System" : RequestSourceMap[requestSource] ?? "Manual";

	let detail = "";
	if (index !== 0 && !isSystem) detail = requestedByName ?? "";
	else if (index === 0 || isLast) detail = "Automatic";
	else detail = `Attempt ${index + 1} Of ${total}`;

	return `${source}${detail ? ` | ${detail}` : ""}`;
};

// Billing follows the same rules as vending, first row included
const buildNotifyMeta = buildTokenMeta;

const retryTitle = (index: number, requestSource: number, initialTitle: string): string => {
	if (index === 0) return initialTitle;
	return requestSource === REQUEST_SOURCE.SYSTEM ? "Auto-Retry" : "Manual Retry";
};

export const mapTokenTimeline = (items?: VendingTokenTimelineItem[]): TimelineEntry[] => {
	const timeline = items ?? [];

	return timeline.map((item, index) => {
		const isCompleted = item.status === 2; // VendingStatus.Completed
		const isPending = item.status === 1; // VendingStatus.InProgress

		return {
			id: item.vendingTransactionId || String(index),
			title: retryTitle(index, item.requestSource, "Initial Vend"),
			createdAt: item.createdAt,
			statusLabel: VendingStatusMap[item.status] ?? "Unknown",
			statusClass: vendingStatusClassName(item.status),
			iconState: isCompleted ? "completed" : isPending ? "pending" : "failed",
			meta: buildTokenMeta(index, timeline.length, item.requestSource, item.requestedByName),
			rawFields: [
				{ label: "Token", value: item.token },
				{ label: "MAP Refund Token", value: item.mapRefundToken },
				{ label: "Remote Token Load Reference", value: item.vendingRemoteTokenLoadId },
				{ label: "Error", value: item.error },
				{ label: "Vending System Reference", value: item.vendingTransactionId, raw: true },
			],
		};
	});
};

export const mapNotifyTimeline = (items?: BillNotifyTimelineItem[]): TimelineEntry[] => {
	const timeline = items ?? [];

	return timeline.map((item, index) => {
		// BillNotifyAttempt has no status column: an attempt with no error message is still in flight
		const isFailed = !item.isSuccess && !!item.errorMessage;

		return {
			id: item.id || String(index),
			title: retryTitle(index, item.requestSource, "Initial Notification"),
			createdAt: item.createdAt,
			statusLabel: item.isSuccess ? "Notified" : isFailed ? "Failed" : "In-Progress",
			statusClass: item.isSuccess ? "completed" : isFailed ? "failed" : "in-progress",
			iconState: item.isSuccess ? "completed" : isFailed ? "failed" : "pending",
			meta: buildNotifyMeta(index, timeline.length, item.requestSource, item.requestedByName),
			rawFields: [
				{ label: "Error", value: item.errorMessage },
				{ label: "Attempt Reference", value: item.id },
			],
		};
	});
};
